/**
 * Tenant Theme Runtime Context
 * ------------------------
 * Phase 4.5 — future white-label support.
 *
 * A pure, in-memory record of "which tenant/theme is currently active,
 * as far as the runtime is concerned." This is deliberately just a
 * store: it does not resolve bindings (that's `tenantThemeResolver`),
 * adapt them into a runtime shape (that's `tenantThemeAdapter`), or
 * drive theme activation (that's `ThemeEngine.activateForTenant`).
 * Nothing here imports any of those, and nothing here is imported by
 * them -- this file has no callers yet and no dependents.
 *
 * No React, no hooks, no external state library, no storage, no
 * network, no ThemeEngine access. Every method is synchronous and
 * side-effect-free apart from mutating this store's own internal
 * field.
 */
/** Safe default: no active tenant, no active theme. */
const EMPTY_CONTEXT = null;
/**
 * Pure runtime store for the active tenant theme context.
 *
 * Holds at most one `{ tenantId, themeId }` pair in memory. Setting a
 * new context replaces the previous one outright; there is no merge
 * or partial-update behavior, since a context describes a single
 * point-in-time binding, not a set of independently-updatable fields.
 */
export class TenantThemeContextStore {
    constructor() {
        this.context = EMPTY_CONTEXT;
    }
    /**
     * Sets the active tenant theme context, replacing whatever was
     * active before.
     */
    setContext(tenantId, themeId) {
        this.context = { tenantId, themeId };
    }
    /**
     * Reads the current active tenant theme context, or `null` if none
     * is active. Returns a fresh copy so callers can't mutate the
     * store's internal state through the returned reference.
     */
    getContext() {
        return this.context === null ? null : { ...this.context };
    }
    /** Clears the active context back to the safe default (no active tenant/theme). */
    clearContext() {
        this.context = EMPTY_CONTEXT;
    }
}
/**
 * Convenience factory for callers who prefer a factory function over
 * `new TenantThemeContextStore()`. Each call produces an independent
 * store instance -- this is not a singleton.
 */
export function createTenantThemeContextStore() {
    return new TenantThemeContextStore();
}
